import { skipToken, useQuery } from '@tanstack/react-query';
import React from 'react';

import type * as api from '@gitbook/api-client';
import { useEventCallback } from '@gitbook/util-react-hooks';
import { SITE_CUSTOMIZATION_ACTIONS } from '@services/content-agents/browser';

import { useNavigation } from '../../../navigation';
import { apiQuery, useCurrentOrganization } from '../../../state';
import { type AgentStateSource, useAgentState } from '../../Agent';
import { SitePublishButton } from '../../Site';
import type { SiteSetupChoice, SiteSetupStepBehavior } from './types';

/**
 * The publish step: the app's own publish button on its card, the conversation told once the site
 * is published, and its choices opening the site's audience settings or the customization cards.
 *
 * @param props.siteId - The setup conversation's site, if it is one.
 * @param props.isCurrent - Whether the conversation is on this step.
 */
export function usePublishStep(props: {
    agent: AgentStateSource;
    siteId: string | undefined;
    isCurrent: boolean;
    postSuggestion: (message: string) => void;
}): SiteSetupStepBehavior {
    const { agent, siteId, isCurrent, postSuggestion } = props;
    const organization = useCurrentOrganization();
    const { navigate } = useNavigation();
    const messageCount = useAgentState(agent, selectMessageCount);
    // The conversation's length when the customization cards were opened: they last until the
    // next message.
    const [customizationOpenedAt, setCustomizationOpenedAt] = React.useState<number | null>(null);

    const reportPublished = useReportSitePublished({
        siteId: isCurrent ? siteId : undefined,
        // The agent learns of the publication only from the conversation, and ends the setup.
        onReport: () => postSuggestion('I published the site'),
    });
    // Memoized so the cards do not re-render with every keystroke.
    const cardAction = React.useMemo(
        () =>
            siteId ? (
                <SiteSetupPublishButton siteId={siteId} onPublished={reportPublished} />
            ) : null,
        [siteId, reportPublished]
    );

    const onChoice = useEventCallback((choice: SiteSetupChoice) => {
        switch (choice.opens) {
            case 'audience-settings':
                if (siteId) {
                    navigate({
                        screen: {
                            type: 'site',
                            organization: organization.id,
                            site: siteId,
                            tab: { type: 'settings', section: 'audience' },
                        },
                    });
                }
                return true;
            case 'customization-actions':
                setCustomizationOpenedAt(messageCount);
                return true;
            default:
                return false;
        }
    });

    return {
        cardAction,
        onChoice,
        question:
            customizationOpenedAt === messageCount
                ? {
                      options: SITE_CUSTOMIZATION_ACTIONS.map(({ label, description }) => ({
                          label,
                          description,
                      })),
                      onAnswer: postSuggestion,
                  }
                : undefined,
    };
}

/**
 * The site's publish button on the step's card: the app's own publish flow, which also offers the
 * trial or the checkout a site's plan needs. Nothing until the site has loaded, nor once it is
 * published, which the conversation is told of instead.
 */
function SiteSetupPublishButton(props: { siteId: string; onPublished: () => void }) {
    const { siteId, onPublished } = props;
    const organization = useCurrentOrganization();
    const { data: site } = useQuery(
        apiQuery('getSiteById', { organizationId: organization.id, siteId })
    );

    if (!site || site.published) {
        return null;
    }

    return (
        <SitePublishButton
            site={site}
            organization={organization}
            canAdminSite={site.permissions.admin}
            onPublished={onPublished}
            kind="primary"
            size="small"
        />
    );
}

/**
 * Report once that the site is published: when its publish button publishes it, and when it already
 * is on arrival, as after a checkout that left the app or a publish from elsewhere, which no
 * callback reports. Here rather than in the button, which the deck draws a second copy of.
 *
 * @param props.siteId - The site, only while the conversation is on the publish step.
 * @returns The report, for the publish button to call once it publishes the site.
 */
function useReportSitePublished(props: {
    siteId: string | undefined;
    onReport: () => void;
}): () => void {
    const { siteId, onReport } = props;
    const organization = useCurrentOrganization();
    const { data: site } = useQuery(
        apiQuery('getSiteById', siteId ? { organizationId: organization.id, siteId } : skipToken)
    );
    const reported = React.useRef(false);

    const report = useEventCallback(() => {
        if (!reported.current) {
            reported.current = true;
            onReport();
        }
    });

    React.useEffect(() => {
        if (site?.published) {
            report();
        }
    }, [site?.published, report]);

    return report;
}

/** The conversation's length, for {@link useAgentState}. Module-level so its identity stays stable. */
function selectMessageCount(state: { messages: api.AIMessage[] }): number {
    return state.messages.length;
}
